import React, { useState } from "react";
import {
  ChevronDown, ChevronRight, CheckCircle2, X, Clock3, Sparkles, PlayCircle,
  AlertTriangle, Database, TrendingUp, ArrowRightLeft, Wallet,
  Activity, ShoppingCart,
} from "lucide-react";
import { Badge } from "./Primitives";
import { formatNumber, cn } from "../lib/utils";
import { approveRec, rejectRec, snoozeRec } from "../lib/enginesApi";
import { OriginBadge, recOrigin } from "../lib/origin.jsx";

// Shared Recommendation card — identical shape across all 7 engines.
// Collapsed by default to keep the Morning Brief scannable; one tap exposes
// the five-field explainability. "Simulate" opens the Digital Twin drawer.

const ENGINE_ICON = {
  demand_forecast:   TrendingUp,
  stockout_adjusted: Activity,
  inventory_health:  Wallet,
  stockout_risk:     AlertTriangle,
  replenishment:     ShoppingCart,
  redistribution:    ArrowRightLeft,
  aging:             Clock3,
};
const ACCENT_RAIL = {
  indigo:   "border-l-[var(--chart-2,theme(colors.indigo.500))]",
  slate:    "border-l-fg/40",
  amber:    "border-l-warning",
  critical: "border-l-critical",
  primary:  "border-l-primary",
  teal:     "border-l-[var(--chart-3,theme(colors.teal.500))]",
  warning:  "border-l-warning",
};
const ACCENT_TEXT = {
  indigo:   "text-info",
  slate:    "text-muted",
  amber:    "text-warning",
  critical: "text-critical",
  primary:  "text-primary",
  teal:     "text-success",
  warning:  "text-warning",
};

function confidencePill(c) {
  if (!c) return null;
  if (c.tier === "insufficient") {
    return <Badge status="warning" testid="confidence-insufficient">insufficient evidence</Badge>;
  }
  if (c.prob != null) {
    const pct = Math.round(c.prob * 100);
    const status = c.tier === "high" ? "success" : c.tier === "medium" ? "info" : "warning";
    return <Badge status={status} testid={`confidence-${c.tier}`}>confidence {pct}%</Badge>;
  }
  return <Badge status="info">confidence {c.tier || "medium"}</Badge>;
}

export default function RecommendationCard({ rec, onSimulate, onChange, actor = "operator" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // "approve" | "reject" | "snooze"
  if (!rec) return null;
  const Icon = ENGINE_ICON[rec.engine] || Sparkles;
  const approval = rec.approval || {};
  const state = approval.state || "pending";
  const already = state !== "pending";

  async function act(fn, kind) {
    setBusy(kind);
    try {
      const updated = await fn(rec.id, { actor });
      onChange && onChange(updated);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div data-testid={`rec-${rec.id}`}
         className={cn("border rounded-sm bg-surface-2/40 transition-colors",
           "border-l-4", ACCENT_RAIL[rec.engine_accent] || "border-l-primary",
           already && "opacity-70",
           rec.shadow && "border-dashed")}>
      <div className="p-3 flex items-start gap-3">
        <button onClick={() => setOpen(o => !o)}
                data-testid={`rec-${rec.id}-toggle`}
                className="w-6 h-6 shrink-0 flex items-center justify-center hover:bg-surface rounded-sm">
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <div className={cn("w-8 h-8 rounded-sm shrink-0 flex items-center justify-center bg-surface-2",
          ACCENT_TEXT[rec.engine_accent] || "text-primary")}>
          <Icon size={15} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge status="neutral" testid={`rec-${rec.id}-engine`}>{rec.engine_label}</Badge>
            {rec.action_class && <Badge status="info">{rec.action_class}</Badge>}
            {rec.shadow && <Badge status="info">shadow</Badge>}
            <OriginBadge {...recOrigin(rec)} testid={`rec-${rec.id}-origin`} />
            {already && <Badge status={state === "approved" ? "success" : state === "rejected" ? "critical" : "info"}>{state}</Badge>}
          </div>
          <div className="text-[13px] font-medium mt-1 leading-snug" data-testid={`rec-${rec.id}-title`}>
            {rec.title}
          </div>
          <div className="text-[11px] text-muted mt-0.5">
            {rec.scope?.sku_name ? `${rec.scope.sku_name} · ` : ""}
            {rec.scope?.branch_name || rec.scope?.receiver_branch || "network"}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9px] uppercase tracking-widest text-muted font-semibold">Expected PHP</div>
          <div className="font-mono-tabular text-sm text-primary font-semibold"
               data-testid={`rec-${rec.id}-value`}>
            ₱{formatNumber(rec.expected_value_php || 0, { compact: true })}
          </div>
          <div className="mt-1">{confidencePill(rec.confidence)}</div>
        </div>
      </div>

      {open && (
        <div className="border-t border-border px-3 py-2.5 bg-bg space-y-2.5" data-testid={`rec-${rec.id}-body`}>
          <Row label="What happened" text={rec.what_happened} />
          <Row label="Why" text={rec.why} />
          <Row label="If nothing changes" text={rec.if_nothing_changes} />
          <Row label="AI recommends" text={rec.ai_recommends} highlight />
          <Row label="Expected business value"
               text={`₱${formatNumber(rec.expected_value_php || 0, { compact: false })} — subject to stated assumptions.`}
               highlight />
          {rec.detail && Object.keys(rec.detail).length > 0 && (
            <details className="text-[11px]">
              <summary className="text-muted cursor-pointer hover:text-primary">Technical detail</summary>
              <pre className="mt-1 font-mono-tabular text-[10.5px] bg-surface-2/60 border border-border rounded-sm p-2 overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(rec.detail, null, 2)}
              </pre>
            </details>
          )}
          {approval.note && (
            <div className="text-[11px] text-muted">
              Note: <span className="italic">{approval.note}</span> — by {approval.actor || "operator"} on {approval.ts?.slice(0, 16).replace("T", " ")}
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button data-testid={`rec-${rec.id}-simulate`}
                    onClick={() => onSimulate && onSimulate(rec)}
                    className="px-3 py-1.5 text-[11px] border border-info text-info hover:bg-info hover:text-primary-fg rounded-sm inline-flex items-center gap-1 transition-colors">
              <PlayCircle size={11} /> Simulate
            </button>
            <button data-testid={`rec-${rec.id}-approve`}
                    disabled={state === "approved" || !!busy}
                    onClick={() => act(approveRec, "approve")}
                    className="px-3 py-1.5 text-[11px] bg-primary text-primary-fg font-semibold hover:opacity-90 rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
              <CheckCircle2 size={11} /> {state === "approved" ? "Approved" : "Approve"}
            </button>
            <button data-testid={`rec-${rec.id}-snooze`}
                    disabled={state === "snoozed" || !!busy}
                    onClick={() => act(snoozeRec, "snooze")}
                    className="px-3 py-1.5 text-[11px] border border-border hover:border-warning hover:text-warning rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
              <Clock3 size={11} /> {state === "snoozed" ? "Snoozed" : "Snooze"}
            </button>
            <button data-testid={`rec-${rec.id}-reject`}
                    disabled={state === "rejected" || !!busy}
                    onClick={() => act(rejectRec, "reject")}
                    className="px-3 py-1.5 text-[11px] border border-border hover:border-critical hover:text-critical rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
              <X size={11} /> {state === "rejected" ? "Rejected" : "Reject"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, text, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">{label}</div>
      <div className={cn("text-[12.5px] leading-relaxed",
        highlight ? "text-fg" : "text-fg/90")}>{text}</div>
    </div>
  );
}
